import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Container,
  Button,
  IconButton,
  Divider,
  Paper
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { signUp } from "../../actions/authActions";
import useForm, { FormContext } from "react-hook-form";

import TextField from "../form/TextField";
import GoogleLogin from "../auth/GoogleLogin";
import Tooltip from "../common/Tooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: "60rem",
    margin: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: theme.spacing(2, 1),
    },
  },
  heading: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
    fontFamily: "Kosugi Maru, sans-serif",
  },
  formContainer: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      width: "100%",
      margin: 0,
      borderRadius: 0,
    },
  },
  accountTypeSection: {
    marginBottom: theme.spacing(3),
  },
  accountTypeLabel: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    fontSize: "1.6rem",
    fontWeight: 600,
  },
  required: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5),
  },
  toggleGroup: {
    width: "100%",
    marginBottom: theme.spacing(1),
    fontSize: "1.6rem",
  },
  toggleButton: {
    width: "50%",
    border: `1px solid ${theme.palette.primary.main}`,
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "1.2rem",
    marginTop: theme.spacing(0.5),
  },
  passwordSection: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  passwordFields: {
    flexGrow: 1,
  },
  visibilityIcon: {
    color: theme.palette.primary.main,
    fontSize: '2rem',
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(1),
    "& svg": {
      fontSize: "2.5rem",
    },
  },
  submitButton: {
    fontSize: "1.6rem",
    padding: theme.spacing(1, 3),
    marginTop: theme.spacing(2),
  },
  dividerSection: {
    margin: theme.spacing(3, 0),
    textAlign: "center",
    position: "relative",
    "& hr": {
      margin: theme.spacing(2, 0),
    },
  },
  orText: {
    fontSize: "1.8rem",
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
}));

function Signup(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const methods = useForm(); // initialise the hook
  const [showPassword, setShowPassword] = useState(false);
  const { register, setValue, errors, handleSubmit, watch } = methods;
  const userType = watch("user_type");

  useEffect(() => {
    register({ name: "user_type" }, { required: true });
  }, [register]);

  const handleUserTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setValue("user_type", newValue);
    }
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = data => {
    let fd = Object.assign({}, data);
    fd.profile = { user_type: fd.user_type };
    fd.password2 = undefined;
    fd.user_type = undefined;
    dispatch(signUp(fd));
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Box className={classes.root}>
        <Typography variant="h4" component="h1" className={classes.heading}>
          アカウント作成
        </Typography>

        <Paper className={classes.formContainer} elevation={1}>
          <FormContext {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box className={classes.accountTypeSection}>
                <Typography className={classes.accountTypeLabel}>
                  <span className={classes.required}>*</span>
                  アカウントタイプを選んでください
                  <Tooltip
                    className={"u-margin-left-small"}
                    iconClass={"fas fa-question-circle has-text-warning"}
                  >
                    <p>
                      仕事を探している方は『求職者』,
                      <br />
                      求人広告を投稿したい方は『企業・雇用主』を選んでください
                    </p>
                  </Tooltip>
                </Typography>
                <ToggleButtonGroup
                  value={userType}
                  exclusive
                  onChange={handleUserTypeChange}
                  className={classes.toggleGroup}
                >
                  <ToggleButton value="Personal" className={classes.toggleButton}>
                    求職者
                  </ToggleButton>
                  <ToggleButton value="Business" className={classes.toggleButton}>
                    企業・雇用主
                  </ToggleButton>
                </ToggleButtonGroup>
                {errors["user_type"] && (
                  <Typography className={classes.errorText}>
                    アカウントタイプを選んでください
                  </Typography>
                )}
              </Box>

              <Box mb={3}>
                <TextField
                  label={
                    userType === "Business"
                      ? "表示名（会社名、雇用者名等）"
                      : "表示名"
                  }
                  type="text"
                  placeholder="サイト内で使用する名前を入力してください"
                  name="name"
                  registerOptions={{ required: true, maxLength: 100 }}
                  fullWidth
                />
              </Box>

              <Box mb={3}>
                <TextField
                  label="Email"
                  type="email"
                  placeholder="Ｅメールアドレスを入力してください"
                  name="email"
                  info="※アカウント認証確認メールを送信する為、必ず実際に使えるメールアドレスをご登録ください"
                  registerOptions={{ required: true, maxLength: 100 }}
                  fullWidth
                />
              </Box>

              <Box className={classes.passwordSection}>
                <Box className={classes.passwordFields}>
                  <Box mb={2}>
                    <TextField
                      label="パスワード"
                      type={showPassword ? "text" : "password"}
                      placeholder="パスワードを入力してください"
                      name="password"
                      registerOptions={{
                        required: true,
                        minLength: 6,
                        maxLength: 50
                      }}
                      fullWidth
                    />
                  </Box>
                  <TextField
                    label="パスワード確認"
                    type={showPassword ? "text" : "password"}
                    placeholder="パスワードをもう一度入力してください"
                    name="password2"
                    customErrorMessage="パスワードが一致しません"
                    registerOptions={{
                      required: true,
                      validate: value => {
                        return value === methods.watch("password");
                      }
                    }}
                    fullWidth
                  />
                </Box>
                <IconButton
                  onClick={togglePassword}
                  className={classes.visibilityIcon}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Box>

              <Box textAlign="center">
                <Button
                  type="submit"
                  className={classes.submitButton}
                  variant="contained"
                  color="primary"
                >
                  登録
                </Button>
              </Box>
            </form>
          </FormContext>
        </Paper>

        <Box className={classes.dividerSection}>
          <Divider />
          <Typography className={classes.orText}>
            もしくは
          </Typography>
        </Box>

        <GoogleLogin userType={userType} />
      </Box>
    </Container>
  );
}

export default Signup;
